// The Segment snippet queues calls in an array until analytics.js loads.
type SegmentAnalytics = unknown[] & {
  [key: string]: unknown;
  initialize?: unknown;
  initialized?: boolean;
  invoked?: boolean;
  methods?: string[];
  factory?: (method: string) => (...args: unknown[]) => unknown;
  load?: (key: string, options?: unknown) => void;
  _loadOptions?: unknown;
  _writeKey?: string;
  SNIPPET_VERSION?: string;
  page?: (properties?: object) => void;
  track?: (event: string, properties?: object) => void;
  identify?: (userId: string) => void;
  user?: () => { id: () => string | null };
  ready?: (callback: () => void) => void;
  reset?: () => void;
};

declare global {
  interface Window {
    analytics?: SegmentAnalytics;
  }
}

const loadSegment = () => {
  const segmentWriteKey = import.meta.env.VITE_SEGMENT_WRITE_KEY;
  if (!segmentWriteKey) return null;

  const globalAnalyticsKey = "analytics";
  const analytics: SegmentAnalytics = (window[globalAnalyticsKey] =
    window[globalAnalyticsKey] || ([] as unknown as SegmentAnalytics));

  if (analytics.initialize || analytics.invoked) return analytics;
  analytics.invoked = true;

  const methods = [
    "trackSubmit",
    "trackClick",
    "trackLink",
    "trackForm",
    "pageview",
    "identify",
    "reset",
    "group",
    "track",
    "ready",
    "alias",
    "debug",
    "page",
    "screen",
    "once",
    "off",
    "on",
    "addSourceMiddleware",
    "addIntegrationMiddleware",
    "setAnonymousId",
    "addDestinationMiddleware",
    "register",
  ];
  analytics.methods = methods;

  const factory = (method: string) => {
    return function (...args: unknown[]) {
      const globalAnalytics = window[globalAnalyticsKey];
      if (globalAnalytics?.initialized) {
        return (globalAnalytics[method] as (...callArgs: unknown[]) => unknown).apply(
          globalAnalytics,
          args
        );
      }

      // Capture page context now, not when the buffered call is eventually sent.
      if (["track", "screen", "alias", "group", "page", "identify"].includes(method)) {
        const canonical = document.querySelector("link[rel='canonical']");
        args.push({
          __t: "bpc",
          c: canonical?.getAttribute("href") || undefined,
          p: location.pathname,
          u: location.href,
          s: location.search,
          t: document.title,
          r: document.referrer,
        });
      }

      args.unshift(method);
      analytics.push(args);
      return analytics;
    };
  };
  analytics.factory = factory;

  for (const key of methods) {
    analytics[key] = factory(key);
  }

  const load = (key: string, options?: unknown) => {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.setAttribute("data-global-segment-analytics-key", globalAnalyticsKey);
    script.src = "https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";

    const first = document.getElementsByTagName("script")[0];
    if (first?.parentNode) {
      first.parentNode.insertBefore(script, first);
    }
    analytics._loadOptions = options;
  };
  analytics.load = load;
  analytics._writeKey = segmentWriteKey;
  analytics.SNIPPET_VERSION = "5.2.1";
  load(segmentWriteKey);

  return analytics;
};

// Keep identity changes ahead of events while the SDK is still loading.
const withSegment = (callback: (client: SegmentAnalytics) => void) => {
  loadSegment()?.ready?.(() => {
    if (window.analytics) callback(window.analytics);
  });
};

export const segment = {
  identify(directoryUuid: string | undefined) {
    withSegment(client => {
      const previousUuid = client.user?.().id();
      // Reset before changing accounts so their anonymous activity is not linked.
      if (previousUuid && previousUuid !== directoryUuid) client.reset?.();
      if (directoryUuid && previousUuid !== directoryUuid) client.identify?.(directoryUuid);
    });
  },
  track(eventName: string, properties: object) {
    withSegment(client => client.track?.(eventName, properties));
  },
  page(properties: object) {
    withSegment(client => client.page?.(properties));
  },
  reset() {
    window.analytics?.ready?.(() => window.analytics?.reset?.());
  },
};
